import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Info, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const InitialSetupPage = () => {
  const [adminKey, setAdminKey] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSetup, setIsCheckingSetup] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check if setup is required
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('superadmin/check-setup');
        
        if (error) {
          console.error('Error checking setup status:', error);
          // If we can't check setup status, assume setup is required for safety
          console.log('Assuming setup is required due to check error');
          setSetupRequired(true);
          setIsCheckingSetup(false);
          return;
        }

        if (!data.setupRequired) {
          // Setup already complete, redirect to login
          toast({
            title: "Setup Complete",
            description: "Super admin already exists. Redirecting to login...",
          });
          setTimeout(() => {
            navigate("/admin/login");
          }, 2000);
          return;
        }

        setSetupRequired(true);
      } catch (error) {
        console.error('Setup check error:', error);
        // If we can't check setup status, assume setup is required for safety
        console.log('Assuming setup is required due to network error');
        setSetupRequired(true);
      } finally {
        setIsCheckingSetup(false);
      }
    };

    checkSetupStatus();
  }, [navigate, toast]);

  const handleSetup = async () => {
    // Validate all required fields
    if (!adminKey.trim()) {
      toast({
        title: "Admin key required",
        description: "Please enter the admin secret key",
        variant: "destructive",
      });
      return;
    }

    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (!password.trim()) {
      toast({
        title: "Password required",
        description: "Please enter a password (minimum 6 characters)",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    if (!name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter your full name",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('superadmin/create', {
        body: {
          adminKey: adminKey.trim(),
          email: email.trim(),
          password: password.trim(),
          name: name.trim(),
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to create super admin');
      }

      toast({
        title: "Success",
        description: data.message || "Super admin created successfully",
        duration: 5000,
      });

      // Store a flag in localStorage to prevent accessing this page again
      localStorage.setItem("initial_setup_complete", "true");

      // Add a small delay to show the success message before redirecting
      setTimeout(() => {
        navigate("/admin/login");
      }, 1500);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create super admin";

      console.error("Setup error:", error);

      toast({
        title: "Setup failed",
        description: errorMessage,
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking setup status
  if (isCheckingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <div className="flex items-center gap-3">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span>Checking setup status...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Only show setup form if setup is required
  if (!setupRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <div className="text-center space-y-3">
              <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto" />
              <p className="text-lg font-medium">Setup Already Complete</p>
              <p className="text-sm text-muted-foreground">Redirecting to login...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Initial Setup
          </CardTitle>
          <CardDescription className="text-center">
            Create the super admin account for the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              This page is only accessible when no super admin account exists.
              Complete this form to create the first administrator account.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter a secure password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminKey">Admin Secret Key</Label>
              <Input
                id="adminKey"
                type="password"
                placeholder="Enter the admin secret key"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !isLoading && handleSetup()
                }
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                This key is required to authorize super admin creation
              </p>
            </div>
          </div>

          <div className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
            <p className="font-medium mb-1">The super admin account will have:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Full system administration privileges</li>
              <li>User management capabilities</li>
              <li>Content and settings management access</li>
              <li>Both Super Admin and Standard User roles</li>
            </ul>
          </div>
        </CardContent>
        <CardFooter>
          <Button className="w-full" onClick={handleSetup} disabled={isLoading}>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span>Creating Super Admin...</span>
              </div>
            ) : (
              "Create Super Admin Account"
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default InitialSetupPage;